import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import AddonsConfigurator from 'src/components/AddonsConfigurator';
import Button from 'src/components/Button';
import QuantityChecker from 'src/components/QuantityChecker';

import theme from 'src/styles/components/Addon.module.scss';

const ConfigurableAddons = ({ virtualId, addons, onAddAddon, onSelectAddon, onRemoveAddon }) => {
    const handleIncreaseQuantity = useCallback((addon) => {
        onSelectAddon(addon.virtualId, { quantity: addon.quantity + 1 });
    });

    const handleDecreaseQuantity = useCallback((addon, position) => {
        if (addon.quantity - 1 === 0) {
            onRemoveAddon(addon.virtualId, position);
        } else {
            onSelectAddon(addon.virtualId, { quantity: addon.quantity - 1 });
        }
    });

    const handleAddAddon = useCallback((addon) => {
        if (addon.maxQuantity > 0) {
            onAddAddon(addon.virtualId);
        }
    });

    const intl = useIntl();

    const addonsToSelect = addons.filter((addon) => !addon.quantity);
    const selectedAddons = addons.filter((addon) => addon.quantity > 0);

    return (
        <div className={theme.container}>
            <div name={virtualId} className={theme.positionsToSelectContainer}>
                {addonsToSelect.map((addon) => (
                    <div
                      key={addon.virtualId}
                      className={classnames(theme.positionContainer, { [theme.disabled]: addon.maxQuantity === 0 })}
                    >
                        <Button
                          className={theme.addIcon}
                          floating
                          mini
                          iconSmall
                          accent
                          icon="icon-plus"
                          onClick={() => handleAddAddon(addon)}
                        />
                        <div className={theme.addonDescription}>
                            {addon.name}
                        </div>
                        {Boolean(addon.price) && (
                            <div className={theme.price}>
                                {'+ '}{intl.formatNumber(addon.price, { style: 'currency', currency: 'PLN' })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {selectedAddons.map((addon, index) => (
                <div key={addon.virtualId} className={theme.selectedPositionsContainer}>
                    <div className={theme.selectedPositionContainer}>
                        <div className={theme.addonDescription}>
                            {addon.name}
                        </div>
                        <div className={theme.quantityCheckerContainer}>
                            <QuantityChecker
                              configurablePosition={addon}
                              position={addonsToSelect.length + index}
                              increaseQuantity={handleIncreaseQuantity}
                              decreaseQuantity={handleDecreaseQuantity}
                              shouldQuantityZeroRemove
                            />
                        </div>
                    </div>
                    <div className={theme.nested}>
                        {addon.addonGroups.map((addonGroup) => (
                            <AddonsConfigurator key={addonGroup.virtualId} {...addonGroup} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

ConfigurableAddons.propTypes = {
    addons: PropTypes.arrayOf(PropTypes.shape({
        addonGroups: PropTypes.arrayOf(PropTypes.object),
        id: PropTypes.string.isRequired,
        maxQuantity: PropTypes.number.isRequired,
        minQuantity: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        parentProductId: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        productId: PropTypes.string.isRequired,
        virtualId: PropTypes.string
    })),
    virtualId: PropTypes.string.isRequired,
    onAddAddon: PropTypes.func.isRequired,
    onSelectAddon: PropTypes.func.isRequired,
    onRemoveAddon: PropTypes.func.isRequired
};

export default ConfigurableAddons;
